import type { RootState } from "../store";
import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import {
  type OptimisticQuestionCacheFields,
  updateQuestionAnswerTreeInCache,
} from "./answerTreeCache";
import { SERIALIZE_BY_HEADER, withHeader } from "./serializedFetchBaseQuery";

export interface Dispatch {
  <TReturn>(
    action: ThunkAction<TReturn, RootState, unknown, UnknownAction>,
  ): TReturn;
  <TAction>(action: TAction): TAction;
}

interface PatchCollection {
  undo: () => void;
}

type UpdateQueryDataThunk = ThunkAction<
  PatchCollection,
  RootState,
  unknown,
  UnknownAction
>;

export function createCleanSlateTracker<TParams>(params: {
  createKey: (params: TParams) => string;
}) {
  const keys = new Set<string>();
  const { createKey } = params;

  return {
    has(keyParams: TParams): boolean {
      return keys.has(createKey(keyParams));
    },
    add(keyParams: TParams): void {
      keys.add(createKey(keyParams));
    },
    delete(keyParams: TParams): void {
      keys.delete(createKey(keyParams));
    },
    createKey,
  };
}

export function patchQueryDataIfPresent<TArgs, TData>(params: {
  dispatch: Dispatch;
  state: RootState;
  args: TArgs;
  selectQueryState: (
    state: RootState,
    args: TArgs,
  ) => { data?: TData | undefined };
  updateQueryData: (
    args: TArgs,
    updater: (draft: TData) => void,
  ) => UpdateQueryDataThunk;
  updater: (draft: TData) => void;
}): PatchCollection | null {
  const { dispatch, state, args, selectQueryState, updateQueryData, updater } =
    params;
  const queryState = selectQueryState(state, args);
  if (!queryState.data) return null;

  return dispatch(updateQueryData(args, updater));
}

export function buildSerializedAnswerMutationHeaders(params: {
  serializeBy: string;
  pruneBy?: string | null | undefined;
  serializeDebounceMs?: number | undefined;
}) {
  const { serializeBy, pruneBy, serializeDebounceMs } = params;

  let headers = withHeader(undefined, SERIALIZE_BY_HEADER, serializeBy);
  if (pruneBy != null) {
    headers = withHeader(headers, "X-Prune-By", pruneBy);
  }
  if (serializeDebounceMs != null) {
    headers = withHeader(
      headers,
      "X-Serialize-Debounce-Ms",
      String(serializeDebounceMs),
    );
  }

  return headers;
}

export function buildUpsertAnswerRequestBody(params: {
  valueTyped?: string | null | undefined;
  questionOptionIds?: string[] | undefined;
  forceCleanSlate: boolean;
}) {
  const { valueTyped, questionOptionIds, forceCleanSlate } = params;

  return {
    valueTyped: valueTyped ?? null,
    questionOptionIds: questionOptionIds ?? null,
    forceCleanSlate,
  };
}

interface OptimisticQuestionOption {
  questionOptionId: string;
}

interface OptimisticAnswer {
  id: string;
  valueTyped: string | null;
  updatedAt: Date;
  answerQuestionOptions: OptimisticQuestionOption[];
}

interface OptimisticQuestion {
  id: string;
  parentQuestionId: string | null;
  questionType: string | null;
  answers: OptimisticAnswer[];
}

type OptimisticCacheQuestion = OptimisticQuestion &
  OptimisticQuestionCacheFields;

interface OptimisticGroup {
  questions: OptimisticCacheQuestion[];
}

export async function runOptimisticAnswerMutation<
  TResult,
  TCacheArgs,
  TKey,
>(params: {
  dispatch: Dispatch;
  getState: () => unknown;
  queryFulfilled: Promise<{ data: TResult }>;
  cache: {
    args: TCacheArgs;
    selectQueryState: (
      state: RootState,
      args: TCacheArgs,
    ) => { data?: OptimisticGroup[] | undefined };
    updateQueryData: (
      args: TCacheArgs,
      updater: (draft: OptimisticGroup[]) => void,
    ) => UpdateQueryDataThunk;
  };
  optimisticTreeUpdate: {
    questionId: string;
    valueTyped: string | null | undefined;
    questionOptionIds: string[] | undefined;
    createOptimisticAnswer: (params: {
      questionId: string;
      normalizedValueTyped: string | null;
      normalizedOptionIds: string[];
      now: Date;
    }) => OptimisticAnswer;
    createAnswerQuestionOption: (params: {
      answerId: string;
      optionId: string;
      now: Date;
    }) => OptimisticQuestionOption;
  };
  cleanSlateKey: TKey;
  cleanSlateTracker: {
    has: (key: TKey) => boolean;
    add: (key: TKey) => void;
    delete: (key: TKey) => void;
  };
  onError?: (params: {
    error: unknown;
    forceCleanSlate: boolean;
    dispatch: Dispatch;
    state: RootState;
    cacheArgs: TCacheArgs;
    affectedQuestionIds: string[];
  }) => void;
  onSuccess?: (params: {
    forceCleanSlate: boolean;
    result: TResult;
    dispatch: Dispatch;
    state: RootState;
    cacheArgs: TCacheArgs;
    affectedQuestionIds: string[];
  }) => void;
  onFinally?: (params: {
    dispatch: Dispatch;
    state: RootState;
    cacheArgs: TCacheArgs;
    affectedQuestionIds: string[];
  }) => void;
  shouldUndoOnError?: (params: {
    dispatch: Dispatch;
    state: RootState;
    cacheArgs: TCacheArgs;
    affectedQuestionIds: string[];
  }) => boolean;
  onOptimisticUpdate?: (params: {
    draft: OptimisticGroup[];
    affectedQuestionIds: string[];
    optimisticNow: Date;
  }) => void;
}): Promise<void> {
  const {
    dispatch,
    getState,
    queryFulfilled,
    cache,
    optimisticTreeUpdate,
    cleanSlateKey,
    cleanSlateTracker,
    onError,
    onSuccess,
    onFinally,
    shouldUndoOnError,
    onOptimisticUpdate,
  } = params;

  const forceCleanSlate = cleanSlateTracker.has(cleanSlateKey);
  let affectedQuestionIds: string[] = [];
  const optimisticNow = new Date();

  const patchCollection = patchQueryDataIfPresent({
    dispatch,
    state: getState() as RootState,
    args: cache.args,
    selectQueryState: cache.selectQueryState,
    updateQueryData: cache.updateQueryData,
    updater: (draft) => {
      const result = updateQuestionAnswerTreeInCache({
        draft,
        questionId: optimisticTreeUpdate.questionId,
        valueTyped: optimisticTreeUpdate.valueTyped,
        questionOptionIds: optimisticTreeUpdate.questionOptionIds,
        forceCleanSlate,
        optimisticNow,
        createOptimisticAnswer: optimisticTreeUpdate.createOptimisticAnswer,
        createAnswerQuestionOption:
          optimisticTreeUpdate.createAnswerQuestionOption,
      });
      affectedQuestionIds = result.affectedQuestionIds;
      onOptimisticUpdate?.({ draft, affectedQuestionIds, optimisticNow });
    },
  });

  try {
    const { data: result } = await queryFulfilled;
    if (forceCleanSlate) {
      cleanSlateTracker.delete(cleanSlateKey);
    }
    onSuccess?.({
      forceCleanSlate,
      result,
      dispatch,
      state: getState() as RootState,
      cacheArgs: cache.args,
      affectedQuestionIds,
    });
  } catch (error) {
    cleanSlateTracker.add(cleanSlateKey);
    const state = getState() as RootState;
    const shouldUndo =
      shouldUndoOnError?.({
        dispatch,
        state,
        cacheArgs: cache.args,
        affectedQuestionIds,
      }) ?? true;
    if (shouldUndo) {
      patchCollection?.undo();
    }
    onError?.({
      error,
      forceCleanSlate,
      dispatch,
      state,
      cacheArgs: cache.args,
      affectedQuestionIds,
    });
  } finally {
    onFinally?.({
      dispatch,
      state: getState() as RootState,
      cacheArgs: cache.args,
      affectedQuestionIds,
    });
  }
}
